import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Box,
  Pagination,
  PaginationItem,
  ThemeProvider,
  createTheme,
  Chip
} from "@mui/material";
import { Search, Close, Edit, Person, Email, Phone, Category } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { getSupportRequestsApi, updateTicketStatusApi, getSupportStatsApi } from "../../api/adminApi";
import DateRangeSelector from "../../components/datePicker";

export default function SupportPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));

  // API query states
  const [tickets, setTickets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setPage(0);
  };

  function formatDate(inputDateStr) {
    if (inputDateStr?.$d !== undefined) {
      const dateObj = new Date(inputDateStr);
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const year = String(dateObj.getFullYear());
      return `${month}-${day}-${year}`;
    } else {
      return "";
    }
  }

  
  const [openModal, setOpenModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newStatus, setNewStatus] = useState("OPEN");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [newCategory, setNewCategory] = useState("Other");

  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0
  });

  const loadStats = () => {
    getSupportStatsApi()
      .then((res) => {
        if (res?.statusCode === 200) {
          setStats(res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to load support stats:", err);
      });
  };

  useEffect(() => {
    loadStats();
  }, []);

  const loadTickets = () => {
    const skip = page * limit;
    getSupportRequestsApi(
      skip,
      limit,
      search,
      statusFilter,
      "",
      sortBy,
      sortOrder,
      formatDate(startDate),
      formatDate(endDate)
    )
      .then((res) => {
        if (res?.statusCode === 404 || res?.statusCode === 401) {
          navigate("/");
        } else {
          setTickets(res?.message || []);
          setTotalCount(res?.count || 0);
        }
      })
      .catch((err) => {
        console.error("Failed to load support requests:", err);
      });
  };

  useEffect(() => {
    loadTickets();
  }, [page, limit, search, statusFilter, sortBy, sortOrder, startDate, endDate]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleEditClick = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setResolutionNotes(ticket.resolutionNotes || "");
    setNewCategory(ticket.category || "Other");
    setOpenModal(true);
  };

  const handleUpdateSubmit = () => {
    if (!selectedTicket) return;
    if (newStatus === "RESOLVED" && !resolutionNotes.trim()) {
      alert("Resolution Notes are required to resolve a ticket.");
      return;
    }
    updateTicketStatusApi(selectedTicket.ticketId, newStatus, resolutionNotes, newCategory)
      .then((res) => {
        setOpenModal(false);
        loadTickets();
        loadStats();
      })
      .catch((err) => {
        console.error("Failed to update ticket status:", err);
      });
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case "RESOLVED":
        return { bg: "#E2FCD5", text: "#227200" };
      case "IN_PROGRESS":
        return { bg: "#FFF1CC", text: "#B78103" };
      case "OPEN":
      default:
        return { bg: "#D0ECFF", text: "#1890FF" };
    }
  };

  const columns = [
    {
      field: "ticketId",
      headerName: "Ticket ID",
      headerClassName: "super-app-theme--header",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: "bold", color: "#1890FF" }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      headerClassName: "super-app-theme--header",
      flex: 1.3,
      minWidth: 170,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    },
    {
      field: "name",
      headerName: "Name",
      headerClassName: "super-app-theme--header",
      flex: 1.1,
      minWidth: 130,
    },
    {
      field: "email",
      headerName: "Email",
      headerClassName: "super-app-theme--header",
      flex: 1.5,
      minWidth: 160,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      headerClassName: "super-app-theme--header",
      flex: 1.0,
      minWidth: 110,
      renderCell: (params) => params.value || "-"
    },
    {
      field: "category",
      headerName: "Category",
      headerClassName: "super-app-theme--header",
      flex: 0.9,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ bgcolor: "#F5F5F5", color: "#666" }}
        />
      )
    },
    {
      field: "subject",
      headerName: "Subject",
      headerClassName: "super-app-theme--header",
      flex: 1.8,
      minWidth: 180,
    },
    {
      field: "status",
      headerName: "Status",
      headerClassName: "super-app-theme--header",
      flex: 1.0,
      minWidth: 110,
      renderCell: (params) => {
        const colors = getStatusChipColor(params.value);
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: colors.bg,
              color: colors.text,
              fontWeight: "bold",
              borderRadius: "4px"
            }}
          />
        );
      }
    },
    {
      field: "action",
      headerName: "Action",
      headerClassName: "super-app-theme--header",
      flex: 0.8,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton onClick={() => handleEditClick(params.row)} size="small">
          <Edit sx={{ fontSize: 24, color: "#4A0072" }} />
        </IconButton>
      )
    }
  ];

  function CustomFooter() {
    const handlePage = (event, value) => {
      setPage(value - 1);
    };
    const paginationTheme = createTheme({
      palette: {
        primary: {
          main: "#d500f9",
        },
      },
    });
    return (
      <ThemeProvider theme={paginationTheme}>
        <Grid
          container
          style={{
            justifyContent: "space-between",
            padding: "15px",
            alignItems: "center"
          }}
        >
          <Grid item>
            {!isSmallerScreen && (
              <Typography
                sx={{
                  color: "#C679F6",
                  fontFamily: "Open Sans",
                  fontSize: "14px",
                }}
              >
                Showing {limit * page + 1} to {Math.min(limit * (page + 1), totalCount)} of {totalCount} requests
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Pagination
              count={Math.ceil(totalCount / limit)}
              shape="rounded"
              color="primary"
              page={page + 1}
              onChange={handlePage}
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  sx={{
                    border: item.type === "page" ? "1px solid #ECEBEB" : "none",
                    color: "#C679F6",
                    backgroundColor: item.selected ? "red" : "white",
                    ...(item.type === "page" && {
                      borderRadius: "4px",
                    }),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </ThemeProvider>
    );
  }

  return (
    <Grid container sx={{ padding: "25px" }}>
      <Grid
        item
        sx={{
          backgroundColor: "#FAF2FF",
          width: "100%",
        }}
      >
        <Grid container direction={"column"}>
          {/* Stats Cards Section */}
          <Grid item sx={{ mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #ECEBEB", boxShadow: "0 4px 10px rgba(0,0,0,0.03)" }}>
                  <Typography variant="body2" sx={{ color: "#8E8D9A", fontWeight: "bold", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>
                    Total Tickets
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "#4A0072", mt: 1 }}>
                    {stats.total}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #ECEBEB", boxShadow: "0 4px 10px rgba(0,0,0,0.03)" }}>
                  <Typography variant="body2" sx={{ color: "#1890FF", fontWeight: "bold", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>
                    Open Tickets
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1890FF", mt: 1 }}>
                    {stats.open}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #ECEBEB", boxShadow: "0 4px 10px rgba(0,0,0,0.03)" }}>
                  <Typography variant="body2" sx={{ color: "#B78103", fontWeight: "bold", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>
                    In Progress Tickets
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "#B78103", mt: 1 }}>
                    {stats.inProgress}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #ECEBEB", boxShadow: "0 4px 10px rgba(0,0,0,0.03)" }}>
                  <Typography variant="body2" sx={{ color: "#227200", fontWeight: "bold", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>
                    Resolved Tickets
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: "#227200", mt: 1 }}>
                    {stats.resolved}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Category Stats Section */}
          {stats.categories && (
            <Grid item sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#4A0072", mb: 1.5, fontFamily: "Open Sans" }}>
                Tickets By Category (Active / Total)
              </Typography>
              <Grid container spacing={3}>
                {["Billing", "Technical", "General", "Other", "Feedback"].map((catName) => {
                  const catData = stats.categories[catName] || { active: 0, total: 0 };
                  return (
                    <Grid item xs={12} sm={6} md={2.4} key={catName}>
                      <Card sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #ECEBEB", boxShadow: "0 4px 10px rgba(0,0,0,0.03)" }}>
                        <Typography variant="body2" sx={{ color: "#8E24AA", fontWeight: "bold", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>
                          {catName} Tickets
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#333", mt: 1 }}>
                          {catData.active} / {catData.total}
                        </Typography>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}

          <Grid item>
            <Card>
              <CardHeader
                sx={{ display: "flex", justifyContent: "space-between" }}
                title={
                  <Typography variant="h5" sx={{ fontWeight: "bold", color: "#4A0072" }}>
                    Support Requests
                  </Typography>
                }
              />
              <Divider />

              <Card sx={{ padding: "15px", alignContent: "center", boxShadow: "none" }}>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* Results Count Select */}
                  <Grid item xs={6} sm={2} md={1.2}>
                    <Box display="flex" alignItems="center">
                      <Typography sx={{ color: "#A6A4A4", mr: 1, whiteSpace: "nowrap" }}>
                        Results:
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={limit}
                          onChange={(e) => {
                            setLimit(e.target.value);
                            setPage(0);
                          }}
                          sx={{ bgcolor: "#F8F8F8" }}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={30}>30</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Status Filter */}
                  <Grid item xs={6} sm={3} md={1.8}>
                    <Box display="flex" alignItems="center">
                      <Typography sx={{ color: "#A6A4A4", mr: 1, whiteSpace: "nowrap" }}>
                        Status:
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={statusFilter}
                          onChange={handleStatusFilterChange}
                          displayEmpty
                          sx={{ bgcolor: "#F8F8F8" }}
                        >
                          <MenuItem value="">All Statuses</MenuItem>
                          <MenuItem value="OPEN">Open</MenuItem>
                          <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                          <MenuItem value="RESOLVED">Resolved</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Sorting Field */}
                  <Grid item xs={6} sm={3} md={1.8}>
                    <Box display="flex" alignItems="center">
                      <Typography sx={{ color: "#A6A4A4", mr: 1, whiteSpace: "nowrap" }}>
                        Sort:
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          sx={{ bgcolor: "#F8F8F8" }}
                        >
                          <MenuItem value="createdAt">Date Created</MenuItem>
                          <MenuItem value="status">Status</MenuItem>
                          <MenuItem value="category">Category</MenuItem>
                          <MenuItem value="ticketId">Ticket ID</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={2} md={1.5}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        sx={{ bgcolor: "#F8F8F8" }}
                      >
                        <MenuItem value="desc">Descending</MenuItem>
                        <MenuItem value="asc">Ascending</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Date Range Selector */}
                  <Grid item xs={12} sm={6} md={3.2}>
                    <DateRangeSelector
                      endDate={endDate}
                      setEndDate={setEndDate}
                      setStartDate={setStartDate}
                      startDate={startDate}
                      handleStartDateChange={handleStartDateChange}
                      handleEndDateChange={handleEndDateChange}
                    />
                  </Grid>

                  {/* Search Bar */}
                  <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                      fullWidth
                      size="small"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search tickets, names, subject..."
                      inputProps={{ maxLength: 100 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton>
                              <Search />
                            </IconButton>
                          </InputAdornment>
                        ),
                        style: { height: "40px", backgroundColor: "#F8F8F8" }
                      }}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Card>
          </Grid>

          {/* Tickets List DataGrid */}
          <Grid item sx={{ mt: 2 }}>
            <Paper
              sx={{
                width: "100%",
                overflow: "hidden",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                "& .super-app-theme--header": {
                  backgroundColor: "#FCF8FF",
                  color: "#83868B",
                  fontFamily: "Open Sans",
                  fontWeight: "bold"
                },
              }}
            >
              <div style={{ height: "60vh", width: "100%" }}>
                <DataGrid
                  rows={tickets}
                  columns={columns}
                  getRowId={(row) => row.ticketId}
                  pagination
                  paginationMode="server"
                  pageSize={limit}
                  onPageChange={(p) => setPage(p)}
                  rowCount={totalCount}
                  components={{
                    Footer: CustomFooter,
                  }}
                  sx={{
                    color: "#666",
                    fontFamily: "Open Sans",
                  }}
                />
              </div>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* Update Support Ticket Dialog */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: "12px", padding: "10px" }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "#4A0072" }}>
            Resolve Ticket: {selectedTicket?.ticketId}
          </Typography>
          <IconButton onClick={() => setOpenModal(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "#FCF8FF", borderRadius: "8px", border: "1px solid #E6D4F0" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#4A0072", mb: 1.5, display: "flex", alignItems: "center" }}>
                  <Person sx={{ fontSize: 18, mr: 0.5 }} /> Customer Details
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                      {selectedTicket?.name || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center" }}><Email sx={{ fontSize: 14, mr: 0.5 }} /> Email</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333", wordBreak: "break-all" }}>
                      {selectedTicket?.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center" }}><Phone sx={{ fontSize: 14, mr: 0.5 }} /> Mobile</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                      {selectedTicket?.mobile || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center" }}><Category sx={{ fontSize: 14, mr: 0.5 }} /> Category</Typography>
                    <Chip
                      label={selectedTicket?.category || "General"}
                      size="small"
                      sx={{ bgcolor: "#E6D4F0", color: "#4A0072", fontWeight: "bold", height: 20, mt: 0.5 }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Subject
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {selectedTicket?.subject}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Description
              </Typography>
              <Paper sx={{ p: 2, bgcolor: "#F9F9F9", border: "1px solid #ECEBEB", whiteSpace: "pre-line" }}>
                <Typography variant="body2">{selectedTicket?.description}</Typography>
              </Paper>
            </Grid>

            {/* Category Select */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Category
                </Typography>
                <Select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                >
                  <MenuItem value="Billing">Billing</MenuItem>
                  <MenuItem value="Technical">Technical</MenuItem>
                  <MenuItem value="General">General</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                  <MenuItem value="Feedback">Feedback</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Status Select */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Status
                </Typography>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="RESOLVED">Resolved</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Resolution/In Progress Notes Input */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                {newStatus === "RESOLVED" ? (
                  <>Resolution Notes<span style={{ color: "red" }}>*</span></>
                ) : newStatus === "IN_PROGRESS" ? (
                  "In Progress Notes"
                ) : (
                  "Notes"
                )}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder={
                  newStatus === "RESOLVED"
                    ? "Write the final solution details, resolution summary, or customer response..."
                    : newStatus === "IN_PROGRESS"
                    ? "Write current progress, troubleshooting steps, or temporary workarounds..."
                    : "Write support action details or notes here..."
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenModal(false)} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSubmit}
            variant="contained"
            sx={{ bgcolor: "#4A0072", "&:hover": { bgcolor: "#6A1B9A" } }}
          >
            Update Ticket
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
